import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../utils/auth';
import { ProductIcon, KnowledgeIcon } from '../components/BrandIcons';
import '../styles/ProductKnowledge.css';

// The Products library (design §3.5). A product is a NAMED GROUP of Orcanos projects;
// a project belongs to at most one product, so unassigned projects are the honest
// signal for "this panel generates without domain knowledge."
export default function ProductsLibrary({ isAdmin = false }) {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [projects, setProjects] = useState([]);      // per-version rows from /api/config/projects
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dialog, setDialog] = useState(null);        // { mode:'create'|'edit', id?, name, projectIds:Set }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, p] = await Promise.all([
        fetch('/api/knowledge', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/config/projects', { credentials: 'include' }).then(r => r.json()),
      ]);
      setProducts(k.products || []);
      setAssigned((k.assigned_project_ids || []).map(String));
      setProjects(p.projects || []);
      setError(null);
    } catch (e) {
      setError('Could not load products.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Distinct Orcanos projects (collapse the per-version rows) → { id, name }.
  const distinctProjects = useMemo(() => {
    const seen = new Map();
    for (const p of projects) {
      const id = String(p.project_id);
      if (!seen.has(id)) seen.set(id, { id, name: p.project_name || `Project ${id}` });
    }
    return Array.from(seen.values());
  }, [projects]);

  const nameOf = useCallback(
    id => (distinctProjects.find(p => p.id === String(id)) || {}).name || `Project ${id}`,
    [distinctProjects],
  );

  const assignedSet = useMemo(() => new Set(assigned.map(String)), [assigned]);
  const unassigned = useMemo(
    () => distinctProjects.filter(p => !assignedSet.has(p.id)),
    [distinctProjects, assignedSet],
  );

  const statusChip = (status) => {
    if (status === 'ready') return <span className="pk-chip pk-chip-ready">✓ Ready</span>;
    if (status === 'stale') return <span className="pk-chip pk-chip-stale">⚠ Stale</span>;
    return <span className="pk-chip pk-chip-none">— No knowledge yet</span>;
  };

  // -------------------------------------------------------------- dialog

  const openCreate = (preselect = []) =>
    setDialog({ mode: 'create', name: '', projectIds: new Set(preselect.map(String)) });

  const openEdit = (product) =>
    setDialog({
      mode: 'edit', id: product.id, name: product.name,
      projectIds: new Set((product.project_ids || []).map(String)),
    });

  // In the dialog a project is selectable when it's unassigned OR already in THIS product.
  const selectableInDialog = useMemo(() => {
    if (!dialog) return [];
    const own = dialog.mode === 'edit'
      ? new Set((products.find(p => p.id === dialog.id)?.project_ids || []).map(String))
      : new Set();
    return distinctProjects.filter(p => !assignedSet.has(p.id) || own.has(p.id));
  }, [dialog, distinctProjects, assignedSet, products]);

  const toggleDialogProject = (id) => {
    setDialog(d => {
      const next = new Set(d.projectIds);
      next.has(id) ? next.delete(id) : next.add(id);
      return { ...d, projectIds: next };
    });
  };

  const saveDialog = async () => {
    if (!dialog.name.trim()) { setError('A product name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = { name: dialog.name.trim(), project_ids: Array.from(dialog.projectIds) };
      const url = dialog.mode === 'create'
        ? '/api/knowledge/products'
        : `/api/knowledge/products/${dialog.id}`;
      const method = dialog.mode === 'create' ? 'POST' : 'PATCH';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      setDialog(null);
      await load();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pk-page">
      <div className="pk-header">
        <button className="pk-back" onClick={() => navigate('/trace/dashboard')}>← Dashboard</button>
        <h1 className="pk-title"><KnowledgeIcon size={24} /> Product Knowledge</h1>
        <div className="pk-header-actions">
          <button className="pk-btn" onClick={() => navigate('/trace/prompts')}>Manage AI prompts</button>
          {isAdmin && (
            <button className="pk-btn pk-btn-primary" onClick={() => openCreate()}>+ New product</button>
          )}
        </div>
      </div>

      <p className="pk-intro">
        A product is a named group of Orcanos projects. Its knowledge document teaches the AI
        about the product and is used as context whenever it drafts requirements or test cases.
      </p>

      {!isAdmin && (
        <div className="pk-readonly">
          👁 View only — Product Knowledge is managed by an Orcanos administrator. You can browse
          products and their documents, but can't make changes.
        </div>
      )}

      {error && <div className="pk-error">{error}</div>}
      {loading ? (
        <div className="pk-loading">Loading…</div>
      ) : (
        <>
          <div className="pk-section-label">PRODUCTS</div>
          {products.length === 0 ? (
            <div className="pk-empty">No products yet. Group your Orcanos projects into a product to start.</div>
          ) : (
            <div className="pk-list">
              {products.map(p => (
                <div key={p.id} className="pk-row" onClick={() => navigate(`/trace/knowledge/${p.id}`)}>
                  <div className="pk-row-main">
                    <div className="pk-row-name"><ProductIcon size={18} /> {p.name}</div>
                    <div className="pk-row-projects">
                      {(p.project_ids || []).length
                        ? p.project_ids.map(nameOf).join(' · ')
                        : 'No projects yet'}
                      <span className="pk-row-count"> ({(p.project_ids || []).length} project{p.project_ids?.length === 1 ? '' : 's'})</span>
                    </div>
                  </div>
                  <div className="pk-row-meta">
                    <span className="pk-row-sources">{p.source_count} source{p.source_count === 1 ? '' : 's'}</span>
                    {statusChip(p.status)}
                    {isAdmin && (
                      <button
                        className="pk-row-edit"
                        title="Edit product"
                        onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                      >✎</button>
                    )}
                    <span className="pk-row-arrow">›</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="pk-section-label">UNASSIGNED PROJECTS — not part of any product yet</div>
          {unassigned.length === 0 ? (
            <div className="pk-empty pk-empty-quiet">Every project is assigned to a product.</div>
          ) : (
            <div className="pk-unassigned">
              <div className="pk-unassigned-names">
                {unassigned.map(p => p.name).join(' · ')}
              </div>
              {isAdmin && (
                <button className="pk-btn" onClick={() => openCreate(unassigned.map(p => p.id))}>
                  Group into a product
                </button>
              )}
            </div>
          )}
        </>
      )}

      {dialog && (
        <div className="pk-modal-backdrop" onClick={() => !saving && setDialog(null)}>
          <div className="pk-modal" onClick={e => e.stopPropagation()}>
            <div className="pk-modal-title">
              {dialog.mode === 'create' ? 'New product' : 'Edit product'}
            </div>
            <label className="pk-field-label">Product name</label>
            <input
              className="pk-input"
              value={dialog.name}
              autoFocus
              placeholder="e.g. Cardiology Monitor"
              onChange={e => setDialog(d => ({ ...d, name: e.target.value }))}
            />
            <label className="pk-field-label">Component projects</label>
            <div className="pk-checklist">
              {selectableInDialog.length === 0 && (
                <div className="pk-empty-quiet">No selectable projects — all are in other products.</div>
              )}
              {selectableInDialog.map(p => (
                <label key={p.id} className="pk-check">
                  <input
                    type="checkbox"
                    checked={dialog.projectIds.has(p.id)}
                    onChange={() => toggleDialogProject(p.id)}
                  />
                  {p.name}
                </label>
              ))}
            </div>
            <div className="pk-modal-actions">
              <button className="pk-btn" onClick={() => setDialog(null)} disabled={saving}>Cancel</button>
              <button className="pk-btn pk-btn-primary" onClick={saveDialog} disabled={saving}>
                {saving ? 'Saving…' : (dialog.mode === 'create' ? 'Create product' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
